// src/lib/studioSettingsStore.js
/**
 * Canonical store for Customize Studio modal settings.
 * Supabase is the source of truth; localStorage is a read-through cache.
 * 
 * Data flow:
 * 1. On boot: Check localStorage cache → show instantly, then refresh from Supabase
 * 2. On save: Update Supabase, invalidate cache, update local state
 * 3. On failure: Fall back to cache
 */

import { supabase } from "@/supabaseClient";

const CACHE_PREFIX = "mudo:studioSettings";

/**
 * Canonical studioSettings shape.
 * Includes all Customize modal fields + updatedAt.
 * Unknown keys in existing settings are preserved.
 */
export function createDefaultStudioSettings() {
  return {
    businessName: "My Studio",
    website: "",
    email: "",
    country: "Australia",
    region: "",
    location: "",
    bio: "",
    practiceTypes: [],
    theme: "Burnt Banksia",
    isDarkMode: false,
    currency: "AUD",
    weight: "g",
    volume: "ml",
    size: "mm",
    productionOutput: 100,
    taxName: "GST",
    taxRatePct: 10,
    taxModeDefault: "inclusive",
    pricingSettings: {},
    logoUrl: "",
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Get cache key for a studio.
 */
function getCacheKey(studioId) {
  return `${CACHE_PREFIX}:${studioId}`;
}

/**
 * Load studio settings from Supabase.
 * Returns { ok: true, settings: {...} } or { ok: false, error, cachedSettings }
 */
export async function loadStudioSettings(studioId) {
  if (!studioId) {
    return { ok: false, error: "studioId required", cachedSettings: null };
  }

  try {
    const { data, error } = await supabase
      .from("studios")
      .select("settings")
      .eq("id", studioId)
      .single();

    if (error) {
      console.warn("[studioSettingsStore] loadStudioSettings error", error);
      // Try cache as fallback
      const cached = getSettingsCache(studioId);
      return { ok: false, error, cachedSettings: cached };
    }

    const settings = {
      ...createDefaultStudioSettings(),
      ...(data?.settings || {}),
    };

    // Update cache
    setSettingsCache(studioId, settings);

    return { ok: true, settings };
  } catch (e) {
    console.warn("[studioSettingsStore] loadStudioSettings exception", e);
    const cached = getSettingsCache(studioId);
    return { ok: false, error: e, cachedSettings: cached };
  }
}

/**
 * Save studio settings to Supabase.
 * Merges with existing settings to preserve unknown keys.
 * Returns { ok: true } or { ok: false, error }
 */
export async function saveStudioSettings(studioId, partial = {}) {
  if (!studioId) {
    return { ok: false, error: "studioId required" };
  }

  try {
    // Read existing to merge
    const { data: existing, error: readErr } = await supabase
      .from("studios")
      .select("settings")
      .eq("id", studioId)
      .single();

    if (readErr) {
      console.warn("[studioSettingsStore] saveStudioSettings read error", readErr);
      return { ok: false, error: readErr };
    }

    // Merge: existing + partial, with updated timestamp
    const merged = {
      ...createDefaultStudioSettings(),
      ...(existing?.settings || {}),
      ...partial,
      updatedAt: new Date().toISOString(),
    };

    // Upsert
    const { error: writeErr } = await supabase
      .from("studios")
      .update({ settings: merged })
      .eq("id", studioId);

    if (writeErr) {
      console.warn("[studioSettingsStore] saveStudioSettings write error", writeErr);
      return { ok: false, error: writeErr };
    }

    // Update cache
    setSettingsCache(studioId, merged);

    return { ok: true };
  } catch (e) {
    console.warn("[studioSettingsStore] saveStudioSettings exception", e);
    return { ok: false, error: e };
  }
}

/**
 * Upload a studio logo to Supabase Storage.
 * Returns { ok: true, url } or { ok: false, error }
 */
export async function uploadStudioLogo({ studioId, file }) {
  if (!studioId || !file) {
    return { ok: false, error: "studioId and file required" };
  }

  try {
    // Validate file type
    const validTypes = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"];
    if (!validTypes.includes(file.type)) {
      return { ok: false, error: "Invalid image type. Use PNG, JPG, WEBP, or SVG." };
    }

    // Validate file size (2MB max)
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      return { ok: false, error: "Image too large. Max 2MB." };
    }

    // Generate filename
    const ext = file.type === "image/svg+xml" ? "svg" : (file.name.split(".").pop() || "png");
    const fileName = `logo-${Date.now()}.${ext}`;
    const path = `${studioId}/${fileName}`;

    // Upload
    const { error: uploadErr } = await supabase.storage
      .from("studio-logos")
      .upload(path, file, { cacheControl: "3600", upsert: true });

    if (uploadErr) {
      console.warn("[studioSettingsStore] uploadStudioLogo error", uploadErr);
      return { ok: false, error: uploadErr };
    }

    // Get public URL
    const { data: publicUrlData } = supabase.storage
      .from("studio-logos")
      .getPublicUrl(path);

    const publicUrl = publicUrlData?.publicUrl || "";
    return { ok: true, url: publicUrl };
  } catch (e) {
    console.warn("[studioSettingsStore] uploadStudioLogo exception", e);
    return { ok: false, error: e };
  }
}

/**
 * Delete a logo from storage using a public URL.
 */
export async function deleteStudioLogoByUrl(publicUrl) {
  if (!publicUrl) return { ok: false, error: "publicUrl required" };

  try {
    // Parse path from public URL
    // e.g., "https://xxx.supabase.co/storage/v1/object/public/studio-logos/abc/logo-123.png"
    // extract: abc/logo-123.png
    const match = publicUrl.match(/studio-logos\/(.+)$/);
    if (!match || !match[1]) {
      return { ok: false, error: "Could not parse logo path from URL" };
    }

    const path = match[1];
    const { error } = await supabase.storage
      .from("studio-logos")
      .remove([path]);

    if (error) {
      console.warn("[studioSettingsStore] deleteStudioLogoByUrl error", error);
      return { ok: false, error };
    }

    return { ok: true };
  } catch (e) {
    console.warn("[studioSettingsStore] deleteStudioLogoByUrl exception", e);
    return { ok: false, error: e };
  }
}

/**
 * Get cached settings from localStorage.
 */
export function getSettingsCache(studioId) {
  try {
    const key = getCacheKey(studioId);
    const cached = localStorage.getItem(key);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
}

/**
 * Set cached settings in localStorage.
 */
export function setSettingsCache(studioId, settings) {
  try {
    const key = getCacheKey(studioId);
    localStorage.setItem(key, JSON.stringify(settings));
  } catch {
    // Silently fail if storage unavailable
  }
}

/**
 * Clear cache for a studio.
 */
export function clearSettingsCache(studioId) {
  try {
    const key = getCacheKey(studioId);
    localStorage.removeItem(key);
  } catch {
    // Silently fail
  }
}
